import { apiBaseOrigin, siteOrigin } from './env';

/**
 * The search-listing preview behind the product form's SEO section (spec §46).
 *
 * Every SEO field on a product is an *override*: blank means "derive it from
 * the product", never "leave it out". The storefront resolves that chain in
 * `website/src/app/products/[slug]/page.tsx` and this module mirrors it, so the
 * preview shows what will actually be served rather than what was typed. If
 * one chain moves the other has to move with it, or the preview starts lying —
 * which is the reason it lives here, in a testable module, rather than inline
 * in the form.
 */

/** Mirrors `SITE_NAME` in `website/src/lib/seo.ts`. */
export const SITE_NAME = 'BULK3.com';

/**
 * What search engines typically show before truncating, and the shortest entry
 * worth writing. Guidance only — the server's limits are far looser (180 and
 * 400) and a merchandiser is allowed to exceed these.
 */
export const TITLE_RANGE = { min: 30, ideal: 60 } as const;
export const DESCRIPTION_RANGE = { min: 70, ideal: 155 } as const;

/** Mirrors `slugify` in `backend/src/common/utils/slug.ts`. */
export function slugify(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 140)
    .replace(/-+$/, '');
}

export function truncate(value: string, limit: number): string {
  return value.length <= limit ? value : `${value.slice(0, limit - 1).trimEnd()}…`;
}

/** The host to show on the social card. A half-typed canonical is not a URL. */
export function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

interface ImageRef {
  type: string;
  url: string;
  displayOrder: number;
}

/**
 * The picture the social card falls back to — the same choice the storefront's
 * `leadImage` makes: the MAIN slot, else the first gallery image in the order
 * an admin arranged, else the hover shot.
 */
export function leadImageUrl(images: ImageRef[] | undefined): string | null {
  if (!images?.length) {
    return null;
  }

  const ordered = images.slice().sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    ordered.find((image) => image.type === 'MAIN')?.url ??
    ordered.find((image) => image.type !== 'MAIN' && image.type !== 'HOVER')?.url ??
    ordered.find((image) => image.type === 'HOVER')?.url ??
    null
  );
}

/** `/static/…` keys are stored root-relative; absolute URLs are left alone. */
export function absoluteImageUrl(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `${apiBaseOrigin}${url}`;
}

/** What the form knows. Strings are raw field values, blank when unset. */
export interface SeoPreviewInput {
  name: string;
  shortDescription: string;
  moq: number | null;
  /** The saved slug, or the one the server would derive from the name. */
  slug: string;
  metaTitle: string;
  metaDescription: string;
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogImageUrl: string;
  images: ImageRef[] | undefined;
}

export interface SeoPreview {
  title: string;
  description: string;
  url: string;
  /** True when the field is derived rather than written — drives the hint. */
  titleInherited: boolean;
  descriptionInherited: boolean;
  social: {
    title: string;
    description: string;
    image: string | null;
  };
}

export function buildSeoPreview(input: SeoPreviewInput): SeoPreview {
  const name = input.name.trim();
  const metaTitle = input.metaTitle.trim();
  const metaDescription = input.metaDescription.trim();

  // A written meta title is rendered verbatim; only an inherited one picks up
  // the site-name template. Someone who counted characters into a 60-character
  // title did not ask for 20 more.
  const title = metaTitle || `${name || 'Untitled product'} | ${SITE_NAME}`;

  const description =
    metaDescription ||
    input.shortDescription.trim() ||
    (name && input.moq
      ? `Buy ${name} in bulk at factory prices. MOQ ${input.moq} pieces.`
      : 'No description yet — write a short description or a meta description.');

  const url = input.canonicalUrl.trim() || `${siteOrigin}/products/${input.slug || '…'}`;

  const image = input.ogImageUrl.trim() || leadImageUrl(input.images);

  return {
    title,
    description,
    url,
    titleInherited: metaTitle === '',
    descriptionInherited: metaDescription === '',
    social: {
      title: input.ogTitle.trim() || title,
      description: input.ogDescription.trim() || description,
      image: image ? absoluteImageUrl(image) : null,
    },
  };
}
